import { IPromise } from 'angular';
import * as React from 'react';
import { Messages } from '../../../codegen/Localize';
import { DialogFooterButtonsWidget, ButtonType, DialogType, DialogPositioningBehavior, ButtonWidget, RadioButtonWithLabelOption, RadioButtonWithLabelGroupWidget, InlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import { ReactModalOptions, ModalInstance, ReactModalService } from '../../../ts/services/ReactModalService';
import { ICollectionInfo } from './ICollectionInfo';
import { CollectionVisibility } from './CollectionModels';
import { CollectionsResourceQuery } from './Collections';
import { cssClass } from '../../../style';

const DIALOG_WIDTH = 400;
const ACTION_DIALOG_TESTID = 'change-collection-visibility-dialog';

// Requires knowledge of inner InlineAlertWidget setup
// Currently widget places a -16px margin botton which doesn't play nice when the
// alert is shown; the dialog doesn't properly expand vertically to include
// the bottom margin cause a scroll bar to appear.
// Overflow is hidden on the dialog body in case this does regress so at least
// no scroll bar will show but it'll be missing the bottom margin
const inlineAlertContentContainer = cssClass('prefix', 'container', {
  $nest: {
    [`.contentContainer`]: {
      marginBottom: '0px'
    }
  }
});

export namespace ChangeCollectionVisibilityDialog {
  export const RESULT_CONFIRM = 'confirm';
  export const RESULT_CANCEL = 'cancel';

  interface DialogState {
    readonly originalVisibility: CollectionVisibility;
    visibility: CollectionVisibility;
  }

  interface ContentsProps {
    readonly originalVisibility: CollectionVisibility;
    updateVisibility: (visibility: CollectionVisibility) => void;
  }

  const radioButtonOptions = (): RadioButtonWithLabelOption<String>[] => {
    return [
      {
        value: 'private',
        testId: 'collection-visibility-private',
        label: Messages.collectionVisibilityPrivate()
      },
      {
        value: 'public',
        testId: 'collection-visibility-public',
        label: Messages.collectionVisibilityPublic()
      }
    ];
  };

  const Contents  = (props: ContentsProps) => {
    const [ isPrivate, setPrivate ] = React.useState(props.originalVisibility === 'private');
    const handleSelect = (value: string) => {
      if (value === 'private') {
        props.updateVisibility('private');
        setPrivate(true);
      } else {
        props.updateVisibility('public');
        setPrivate(false);
      }
    };
    return (
      <div style={{overflowY: 'hidden'}}>
        <div>
          {Messages.collectionsVisibilityDialogHeader()}
        </div>
        <RadioButtonWithLabelGroupWidget
          aria-label={Messages.collectionsVisibilityDialogTitle()}
          buttonList={radioButtonOptions()}
          checkedIndex={isPrivate ? 0 : 1}
          handleChange={handleSelect}
        />
        {isPrivate && props.originalVisibility === 'public' &&
          <InlineAlertWidget
            className={inlineAlertContentContainer}
            testId={'collections-visibility'}
            type={InlineAlertType.Warning}
            style={{ marginTop: '8px'}}
          >
            {Messages.collectionsVisibilityDialogAlert()}
          </InlineAlertWidget>
        }
      </div>
    );
  };

  interface FooterProps {
    onCancel: () => void;
    onOk: () => void;
    updateVisibility: (visibility: CollectionVisibility) => void;
    state: DialogState;
  }
  const Footer = (props: FooterProps) => {
    return <DialogFooterButtonsWidget
        buttons={[
          {
            handleClick: props.onCancel,
            buttonType: ButtonType.Outline,
            children: Messages.cancel(),
            testId: `${ACTION_DIALOG_TESTID}-cancel`,
          },
          {
            handleClick: async () => {
              if (props.state.originalVisibility !== props.state.visibility) {
                await props.updateVisibility(props.state.visibility);
              }
              props.onOk();
            },
            buttonType: ButtonType.Primary,
            children: Messages.save(),
            testId: `${ACTION_DIALOG_TESTID}-ok`,
          },
        ]}
        testId={`${ACTION_DIALOG_TESTID}-footer`}
      />;
  };

  interface OptionsProps {
    collection: ICollectionInfo;
    onCancel: () => void;
    onOk: () => void;
    setUpdatedVisibility: (visibility: CollectionVisibility) => void;
  }

  export function Options(props: OptionsProps): ReactModalOptions {

    const state: DialogState = {
      originalVisibility: props.collection.visibility,
      visibility: props.collection.visibility
    };
    const updateVisibility = (visibility: CollectionVisibility) => {
      state.visibility = visibility;
    };

    return {
      title: Messages.collectionsVisibilityDialogTitle(),
      testId: ACTION_DIALOG_TESTID,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: DIALOG_WIDTH + 'px' },
      dialogContent: <Contents originalVisibility={state.originalVisibility} updateVisibility={updateVisibility} />,
      dialogContentStyle: { padding: 0 },
      footerContent: <Footer onCancel={props.onCancel} onOk={props.onOk} state={state} updateVisibility={props.setUpdatedVisibility}/>
    };
  }

  export function open(collection: ICollectionInfo): IPromise<any> {
    const setUpdatedVisibility = async (visibility: CollectionVisibility) => {
      const updatedCollectionData = {
        visibility
      };
      await new CollectionsResourceQuery().update(collection.luid, updatedCollectionData);
    };
    return openInternal(collection, setUpdatedVisibility);
  }

  function openInternal(collection: ICollectionInfo, setUpdatedVisibility: (visibility: CollectionVisibility) => void): IPromise<any> {
    let modalInstance: ModalInstance;

    const onCancel = (): void => {
      modalInstance && modalInstance.close(RESULT_CANCEL);
    };
    const onOk = (): void => {
      modalInstance && modalInstance.close(RESULT_CONFIRM);
    };

    const optionProps: OptionsProps = {
      collection, onCancel, onOk, setUpdatedVisibility
    };
    modalInstance = ReactModalService.open(Options(optionProps));
    return modalInstance.result;
  }
}
